import {
    createContext,
    FC,
    PropsWithChildren,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useDispatch } from 'react-redux';

import { getKeycloakInstance } from '../services';
import { KeycloakToken, MembershipType, User } from '../types';

import {
    setRealmRoles,
    setUser as setKeycloakUser,
    unsetUser,
} from './authSlice';
import { AuthGate } from '../membership';

export interface AuthContextProps {
    isAuthenticated: boolean | undefined;
    user: User | null;
    login: () => void;
    logout: () => void;
}

export const AuthContext = createContext<AuthContextProps>({
    isAuthenticated: false,
    user: null,
    login: () => {},
    logout: () => {},
});

/**
 * Time in milliseconds to debounce the activity-based token refresh.
 */
const ACTIVITY_DEBOUNCE_MS = 30000;

/**
 * Minimum time in seconds that the token can be considered valid before refreshing it.
 */
const ACTIVITY_MIN_VALIDITY = 1800;

type AuthProviderProps = {
    keycloakUrl: string;
    keycloakRealm: string;
    keycloakClientId: string;
    redirectIfUnauthenticated?: boolean;
    allowedMemberships?: MembershipType[];
    enableActivityBasedRefresh?: boolean;
    /**
     * Called when an unexpected auth-related logout occurs (e.g. token refresh failure,
     * auth refresh error). Not called on user-initiated logouts.
     */
    onUnexpectedLogout?: () => void;
};

export const AuthProvider: FC<PropsWithChildren<AuthProviderProps>> = ({
    keycloakUrl,
    keycloakRealm,
    keycloakClientId,
    children,
    redirectIfUnauthenticated = true,
    allowedMemberships,
    enableActivityBasedRefresh = true,
    onUnexpectedLogout,
}) => {
    const dispatch = useDispatch();

    const keycloakService = useRef(
        typeof window === 'undefined'
            ? null
            : getKeycloakInstance(keycloakUrl, keycloakRealm, keycloakClientId),
    ).current;

    const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>(
        undefined,
    );
    const [user, setUser] = useState<User | null>(null);

    const isInitialized = useRef(false);

    const handleLogout = useCallback(() => {
        setIsAuthenticated(false);
        setUser(null);
        dispatch(unsetUser());
    }, [dispatch]);

    const initKeycloak = async () => {
        try {
            if (isInitialized.current || !keycloakService) return;
            isInitialized.current = true;

            keycloakService.onTokenExpired = () => {
                keycloakService.updateToken(5).catch(() => {
                    onUnexpectedLogout?.();
                    keycloakService.logout();
                    handleLogout();
                });
            };
            keycloakService.onAuthRefreshError = () => {
                onUnexpectedLogout?.();
                keycloakService.logout();
                handleLogout();
            };
            keycloakService.onAuthLogout = handleLogout;

            const authenticated = await keycloakService.init({
                onLoad: redirectIfUnauthenticated
                    ? 'login-required'
                    : 'check-sso',
                silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
                checkLoginIframe: true,
                flow: 'standard',
                pkceMethod: 'S256',
            });

            setIsAuthenticated(authenticated);

            const tokenParsed = keycloakService.tokenParsed as KeycloakToken;
            if (authenticated && tokenParsed) {
                const { user } = tokenParsed;

                setUser(user);

                dispatch(setKeycloakUser({ user }));

                const roles =
                    tokenParsed?.resource_access?.['realm-management']?.roles;
                if (roles) {
                    dispatch(setRealmRoles({ roles }));
                }

                /**
                 * Force-refresh the token on first load to extend its lifetime.
                 * updateToken(-1) always triggers a refresh regardless of remaining validity.
                 * Failure is non-critical — the normal onTokenExpired handler will manage expiry.
                 */
                keycloakService.updateToken(-1).catch(() => {
                    /* Token refresh on load failed — non-critical, continue normally */
                });
            } else {
                dispatch(unsetUser());
            }
        } catch (error) {
            setIsAuthenticated(false);
            return;
        }
    };

    useEffect(() => {
        initKeycloak();

        return () => {
            if (!keycloakService) return;
            keycloakService.onTokenExpired = undefined;
            keycloakService.onAuthRefreshError = undefined;
            keycloakService.onAuthLogout = undefined;
        };
    }, []);

    useEffect(() => {
        if (
            !isAuthenticated ||
            !enableActivityBasedRefresh ||
            !keycloakService
        ) {
            return;
        }

        let activityTimeout: ReturnType<typeof setTimeout>;

        const handleActivity = () => {
            clearTimeout(activityTimeout);
            activityTimeout = setTimeout(async () => {
                try {
                    await keycloakService.updateToken(ACTIVITY_MIN_VALIDITY);
                } catch {
                    /**
                     * Token refresh failed — the onTokenExpired handler will deal with this.
                     */
                }
            }, ACTIVITY_DEBOUNCE_MS);
        };

        const events = ['mousedown', 'keydown', 'scroll'];
        events.forEach((event) =>
            window.addEventListener(event, handleActivity, { passive: true }),
        );

        return () => {
            clearTimeout(activityTimeout);
            events.forEach((event) =>
                window.removeEventListener(event, handleActivity),
            );
        };
    }, [isAuthenticated, enableActivityBasedRefresh]);

    if (redirectIfUnauthenticated && isAuthenticated === undefined) {
        return null;
    }

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user,
                login: () => keycloakService?.login(),
                logout: () => keycloakService?.logout(),
            }}
        >
            <AuthGate
                allowedMemberships={allowedMemberships}
                isAuthenticated={isAuthenticated}
            >
                {children}
            </AuthGate>
        </AuthContext.Provider>
    );
};
